import type { ReactNode } from "react";
import { RotateCcwIcon, SaveIcon, ShuffleIcon } from "lucide-react";

import { ShopGearSlot } from "./ShopGearSlot";
import type { ShopCollectionStats, ShopSlot, ShopSlotItem } from "./types";

import { Column } from "@/components/Column";
import { Row } from "@/components/Row";
import { Text } from "@/components/Text";
import { Button } from "@ui/button";

interface ShopSheetSlot {
  cost: number;
  isChanged: boolean;
  item: ShopSlotItem | null;
}

// Slots sit where the item lands on the pet, WoW character-sheet style.
const slotLayout: {
  slot: ShopSlot;
  label: string;
  className: string;
  isClearable: boolean;
}[] = [
  {
    slot: "TOPPER",
    label: "Head",
    className: "col-start-2 row-start-1",
    isClearable: true,
  },
  {
    slot: "ACCESSORY_2",
    label: "Accessory 2",
    className: "col-start-3 row-start-3 self-end",
    isClearable: true,
  },
  {
    slot: "ACCESSORY_1",
    label: "Accessory 1",
    className: "col-start-3 row-start-2",
    isClearable: true,
  },
  {
    slot: "PET",
    label: "Pet",
    className: "col-start-1 row-start-3 self-end",
    isClearable: false,
  },
  {
    slot: "COLOR",
    label: "Color",
    className: "col-start-1 row-start-2",
    isClearable: false,
  },
];

export const ShopCharacterSheet = ({
  activeSlot,
  adjustControls,
  isResetDisabled,
  isSaveDisabled,
  preview,
  saveLabel,
  slots,
  stats,
  subtitle,
  title,
  onReset,
  onSave,
  onShuffle,
  onSlotClear,
  onSlotSelect,
}: {
  activeSlot: ShopSlot;
  adjustControls: ReactNode;
  isResetDisabled: boolean;
  isSaveDisabled: boolean;
  preview: ReactNode;
  saveLabel: string;
  slots: Record<ShopSlot, ShopSheetSlot>;
  stats: ShopCollectionStats;
  subtitle: string;
  title: string;
  onReset: () => void;
  onSave: () => void;
  onShuffle: () => void;
  onSlotClear: (slot: ShopSlot) => void;
  onSlotSelect: (slot: ShopSlot) => void;
}) => {
  return (
    <Column className="min-h-0 w-[420px] shrink-0 gap-3 overflow-hidden border-4 border-slate-900 bg-[#dff3ff] p-4 shadow-[6px_6px_0_#172033]">
      <div className="grid grid-cols-[76px_1fr_76px] grid-rows-[auto_1fr_auto] items-center justify-items-center gap-2">
        {/* Shares the top row with the Head slot; spans into the empty space beside it. */}
        <Column className="col-span-2 col-start-1 row-start-1 max-w-[150px] gap-0.5 self-start justify-self-start">
          <Text className="font-mono text-sm font-black uppercase leading-tight tracking-[0.14em] text-slate-900">
            {title}
          </Text>
          <Text className="font-mono text-[9px] font-black uppercase tracking-[0.14em] text-orange-600">
            {subtitle}
          </Text>
        </Column>
        {slotLayout.map(({ slot, label, className, isClearable }) => (
          <ShopGearSlot
            key={slot}
            className={className}
            cost={slots[slot].cost}
            isActive={activeSlot === slot}
            isChanged={slots[slot].isChanged}
            item={slots[slot].item}
            label={label}
            onClear={
              isClearable
                ? () => {
                    onSlotClear(slot);
                  }
                : undefined
            }
            onSelect={() => {
              onSlotSelect(slot);
            }}
          />
        ))}
        <Column className="relative col-start-2 row-span-2 row-start-2 h-52 w-52 items-center justify-center overflow-hidden border-4 border-slate-900 bg-white shadow-[4px_4px_0_#172033]">
          {preview}
        </Column>
      </div>

      {/* Reserved so the drag toolbar appearing doesn't shove the rows below. */}
      <Column className="min-h-[38px] justify-center">{adjustControls}</Column>

      <Row className="gap-2">
        <Button
          type="button"
          variant="outlineDark"
          onClick={onShuffle}
          className="h-10 gap-2 rounded-none border-2 border-slate-900 bg-white px-3 font-mono text-[10px] font-black uppercase tracking-[0.1em] text-slate-900 shadow-[3px_3px_0_#172033] hover:bg-orange-50"
        >
          <ShuffleIcon className="h-4 w-4" />
          Shuffle
        </Button>
        <Button
          type="button"
          variant="outlineDark"
          disabled={isResetDisabled}
          onClick={onReset}
          className="h-10 gap-2 rounded-none border-2 border-slate-900 bg-white px-3 font-mono text-[10px] font-black uppercase tracking-[0.1em] text-slate-900 shadow-[3px_3px_0_#172033] hover:bg-orange-50 disabled:opacity-50"
        >
          <RotateCcwIcon className="h-4 w-4" />
          Reset
        </Button>
        <Button
          type="button"
          variant="outlineDark"
          disabled={isSaveDisabled}
          onClick={onSave}
          className="h-10 flex-1 gap-2 rounded-none border-2 border-slate-900 bg-slate-900 font-mono text-xs font-black uppercase tracking-[0.12em] text-orange-50 shadow-[3px_3px_0_#f97316] hover:bg-slate-800 hover:text-orange-50 disabled:opacity-50"
        >
          <SaveIcon className="h-4 w-4" />
          {saveLabel}
        </Button>
      </Row>

      <Row className="gap-2">
        <ShopSheetStat
          label="Collected"
          value={`${stats.collected.toString()}/${stats.total.toString()}`}
        />
        <ShopSheetStat label="Legendary" value={stats.legendary.toString()} />
        <ShopSheetStat label="Exclusive" value={stats.exclusive.toString()} />
      </Row>
    </Column>
  );
};

// One line per stat so the sheet fits without scrolling.
const ShopSheetStat = ({ label, value }: { label: string; value: string }) => (
  <Row className="flex-1 items-baseline justify-center gap-1.5 border-2 border-slate-900 bg-white px-2 py-1">
    <Text className="font-mono text-xs font-black tabular-nums text-slate-900">
      {value}
    </Text>
    <Text className="font-mono text-[8px] font-black uppercase tracking-[0.1em] text-slate-500">
      {label}
    </Text>
  </Row>
);
